import {
  Stack,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { FaFileExcel } from "react-icons/fa";
import dayjs from "dayjs";
import { styled } from "@mui/system";
import { useState, useMemo } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { enIN } from "date-fns/locale";
import { useSettings } from "../../../context/SettingsContext";
import { getStatusUI, capitalize } from "../../../config/leadStatus";

const IOSSegment = styled(ToggleButtonGroup)(() => ({
  background: "#E5E7EB",
  borderRadius: 999,
  padding: 4,
  "& .MuiToggleButton-root": {
    border: 0,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "none",
  },
  "& .Mui-selected": {
    background: "#ffffff !important",
    color: "#111827",
    boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
  },
}));

export default function Header({
  parts = [],
  theme,
  range,
  setRange,
  rangeValue,
  setRangeValue,
  searchParams,
  navigate,
  status,
  setStatus,
  paymentStatus,
  setPaymentStatus,
  service,
  setService,
  source,
  setSource,
  exportExcel,
  exportMuiPdf,
  setExporting,
  setLabel,
  setProgress,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { services, dynamicStatus, lead_features } = useSettings();

  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");

  const dateLabel = useMemo(() => {
    if (!fromParam || !toParam) return null;
    const f = dayjs(fromParam);
    const t = dayjs(toParam);
    if (!f.isValid() || !t.isValid()) return null;

    if (range === "day") return f.format("DD MMM YYYY");
    if (range === "week" || range === "custom")
      return f.isSame(t, "day")
        ? f.format("DD MMM YYYY")
        : `${f.format("DD MMM YYYY")} – ${t.format("DD MMM YYYY")}`;
    if (range === "month") return f.format("MMMM YYYY");
    return null;
  }, [fromParam, toParam, range]);

  const finalParts = useMemo(() => {
    const base = [...parts];
    if (dateLabel) base.push(dateLabel);
    return base;
  }, [parts, dateLabel]);

  const selection = {
    startDate: rangeValue[0]?.toDate() || new Date(),
    endDate: rangeValue[1]?.toDate() || new Date(),
    key: "selection",
  };

  const applyPresetRange = (v) => {
    const now = dayjs();
    let s;
    let e;

    if (v === "day") {
      s = now.startOf("day");
      e = now.endOf("day");
    }
    if (v === "week") {
      s = now.startOf("week");
      e = now.endOf("week");
    }
    if (v === "month") {
      s = now.startOf("month");
      e = now.endOf("month");
    }

    if (!s || !e) return;

    setRangeValue([s, e]);

    const params = new URLSearchParams(searchParams);
    params.set("from", s.toISOString());
    params.set("to", e.toISOString());
    params.set("range", v);
    navigate({ search: params.toString() }, { replace: true });
  };

  const sources = useMemo(() => {
    return (lead_features?.sources || [])
      .filter((s) => s.enabled)
      .sort((a, b) => a.order - b.order);
  }, [lead_features]);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      gap={2}
      mb={2}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 6,
            height: "70%",
            width: 4,
            borderRadius: 2,
            background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.main})`,
          }}
        />

        <Typography
          sx={{
            ml: 1.5,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
          }}
        >
          {finalParts.slice(0, -1).join(" · ") || "Overview"}
        </Typography>

        <Typography
          sx={{
            ml: 1.5,
            fontWeight: 650,
            letterSpacing: "-0.03em",
            fontSize: { xs: 22, sm: 28 },
            lineHeight: 1.05,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {finalParts[finalParts.length - 1]}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1.2}
        alignItems="center"
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        sx={{
          px: 2.2,
          py: range === "custom" ? 0.4 : 0.8,
          borderRadius: 3,
          bgcolor: `${theme.palette.primary.main}0F`,
          border: `1px solid ${theme.palette.primary.main}26`,
          boxShadow: `0 10px 24px ${theme.palette.primary.main}14`,
          overflowX: "auto",
          rowGap: { xs: 1.2, md: 0 },
        }}
      >
        <IOSSegment
          value={range}
          exclusive
          onChange={(e, v) => {
            if (!v) return;
            setRange(v);
            if (v === "custom") setAnchorEl(e.currentTarget);
            else {
              setAnchorEl(null);
              applyPresetRange(v);
            }
          }}
          sx={{
            bgcolor: "#fff",
            "& .MuiToggleButton-root": {
              color: theme.palette.primary.main,
              fontWeight: 700,
            },
            "& .Mui-selected": {
              background: theme.palette.primary.main + " !important",
              color: "#fff !important",
              boxShadow: `0 6px 18px ${theme.palette.primary.main}55`,
            },
          }}
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
          <ToggleButton
            value="custom"
            onClick={(e) => {
              if (range === "custom") setAnchorEl(e.currentTarget);
            }}
          >
            Custom
          </ToggleButton>
        </IOSSegment>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <DateRange
            ranges={[selection]}
            locale={enIN}
            moveRangeOnFirstSelection={false}
            showMonthAndYearPickers={false}
            rangeColors={[theme.palette.primary.main]}
            onChange={({ selection }) => {
              const s = dayjs(selection.startDate).startOf("day");
              const e = dayjs(selection.endDate).endOf("day");

              setRangeValue([s, e]);

              const params = new URLSearchParams(searchParams);
              params.set("from", s.toISOString());
              params.set("to", e.toISOString());
              params.set("range", "custom");

              navigate({ search: params.toString() }, { replace: true });
            }}
          />
        </Popover>

        <Select
          size="small"
          value={service || "all"}
          onChange={(e) => setService(e.target.value)}
          sx={{
            minWidth: 140,
            borderRadius: 999,
            bgcolor: "#fff",
            px: 1,
            border: `1px solid ${theme.palette.primary.main}`,
            "& fieldset": { border: "none" },
            textTransform: "capitalize",
          }}
        >
          <MenuItem value="all">All Services</MenuItem>
          {services?.map((s) => (
            <MenuItem
              key={s}
              value={s}
              sx={{
                textTransform: "capitalize",
              }}
            >
              {s}
            </MenuItem>
          ))}
        </Select>

        <Select
          size="small"
          value={paymentStatus || "all"}
          onChange={(e) => setPaymentStatus(e.target.value)}
          sx={{
            minWidth: 140,
            borderRadius: 999,
            bgcolor: "#fff",
            px: 1,
            border: `1px solid ${theme.palette.primary.main}`,
            "& fieldset": { border: "none" },
            textTransform: "capitalize",
          }}
        >
          <MenuItem value="all">All Payments</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="partial">Partial</MenuItem>
          <MenuItem value="unpaid">Unpaid</MenuItem>
        </Select>

        <Select
          size="small"
          value={source || "all"}
          onChange={(e) => {
            const v = e.target.value;
            setSource(v);

            const params = new URLSearchParams(searchParams);
            if (v === "all") params.delete("source");
            else params.set("source", v);

            navigate({ search: params.toString() }, { replace: true });
          }}
          sx={{
            minWidth: 140,
            borderRadius: 999,
            bgcolor: "#fff",
            px: 1,
            border: `1px solid ${theme.palette.primary.main}`,
            "& fieldset": { border: "none" },
            textTransform: "capitalize",
          }}
        >
          <MenuItem value="all">All Sources</MenuItem>
          {sources.map((s) => (
            <MenuItem key={s.key} value={s.key}>
              {s.label}
            </MenuItem>
          ))}
        </Select>

        <Button
          onClick={exportExcel}
          startIcon={<FaFileExcel style={{ fontSize: 18 }} />}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            px: 2.4,
            height: 34,
            bgcolor: theme.palette.primary.main,
            color: "#fff",
          }}
        >
          Excel
        </Button>

        <Button
          onClick={async () => {
            setExporting(true);
            setLabel("Generating PDF");
            setProgress(0);
            await exportMuiPdf({
              elementId: "pdf-root",
              fileName: `Payments-Report-${dayjs().format("DD-MM-YYYY")}.pdf`,
              onProgress: setProgress,
            });
            setExporting(false);
          }}
          startIcon={<PictureAsPdfIcon />}
          sx={{
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
            px: 2.4,
            height: 34,
            border: `1px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
            bgcolor: "#fff",
          }}
        >
          PDF
        </Button>
      </Stack>
    </Stack>
  );
}
